import React from 'react';
import { Download, LogOut, Search, Unplug } from 'lucide-react';

/**
 * Per-view toolbar shared across Classic / Pro / Positions. Each view
 * renders one instance just above its table, after the hero banner.
 * Owns: count label, search, IST clock, Exit All.
 *
 * Refresh + Reconciliation status pill live at the page level
 * (SchedulePage actions) — they're data-control concerns, not
 * table-scope controls, so grouping them with Scope/View in the page
 * chrome keeps the toolbar focused on table operations.
 *
 * Count label flips by view at the call site: "N Strategies" for
 * Classic/Pro, "N Positions" for Positions. Exit All is hidden on
 * historical dates (caller passes `onExitAll={undefined}`).
 */
export interface SharedToolbarProps {
  countLabel: string;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  currentIstHms: string | null;
  onExitAll?: () => void;
  exitableCount: number;
  exitAllPending: boolean;
  /** Terminate All (ALL scope) — platform-only stop-watching across the whole
   *  book. The exact count is confirmed in the dialog, so no count is passed. */
  onTerminateAll?: () => void;
  isToday: boolean;
  /** Positions tab only: when provided, renders a "Export CSV" button.
   *  Classic/Pro callers omit it and the button doesn't render. */
  onExportCsv?: () => void;
  /** Count of rows the export would emit; drives the disabled state +
   *  tooltip. Only consulted when `onExportCsv` is set. */
  exportRowCount?: number;
}

const SharedToolbar: React.FC<SharedToolbarProps> = ({
  countLabel, searchQuery, onSearchChange,
  currentIstHms,
  onExitAll, exitableCount, exitAllPending, onTerminateAll, isToday,
  onExportCsv, exportRowCount = 0,
}) => (
  <div className="flex items-center justify-between gap-3 flex-wrap bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3">
    <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 tabular-nums">
      {countLabel}
    </div>
    <div className="flex items-center gap-2 flex-wrap">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search basket, strategy, symbol, client…"
          className="pl-9 pr-3 h-9 text-xs w-72 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      {isToday && currentIstHms && (
        <span className="text-xs font-mono text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap">
          IST {currentIstHms}
        </span>
      )}
      {onExitAll && (() => {
        const exitDisabledReason = exitAllPending
          ? 'Exit All in progress…'
          : exitableCount === 0
            ? 'No open positions eligible to exit'
            : undefined;
        return (
          <button
            type="button"
            onClick={onExitAll}
            disabled={exitableCount === 0 || exitAllPending}
            title={exitDisabledReason}
            className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900/40 disabled:cursor-not-allowed rounded-md transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Exit All ({exitableCount})
          </button>
        );
      })()}
      {onTerminateAll && (
        <button
          type="button"
          onClick={onTerminateAll}
          disabled={exitAllPending}
          title="Stop the platform from managing every position (does not touch the broker)"
          className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
        >
          <Unplug className="w-3.5 h-3.5" />
          Terminate All
        </button>
      )}
      {onExportCsv && (
        <button
          type="button"
          onClick={onExportCsv}
          disabled={exportRowCount === 0}
          className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
          {exportRowCount > 0 ? `Export CSV (${exportRowCount})` : 'Export CSV'}
        </button>
      )}
    </div>
  </div>
);

export default SharedToolbar;
